enum ControlledAttr {
    Visibility = 'visibility',
    Disabled = 'disabled',
}

enum TooltipType {
    PASSWORD_REPEAT = 'PASSWORD_REPEAT',
    PASSWORD_STRENGTH = 'PASSWORD_STRENGTH',
}

const ATTRIBUTE_CONTROLLED_BY = 'data-klt-c-form-fields-controlled-by';
const ATTRIBUTE_CONTROL_ATTRIBUTE = 'data-klt-c-form-fields-controlled-attr';
const ATTRIBUTE_INPUT_TOOLTIP = 'data-klt-c-has-tooltip';
const ATTRIBUTE_PASSWORD_TOOLTIP = 'data-klt-c-password-tooltip';
const ATTRIBUTE_PASSWORD_REVEAL_TRIGGER = 'data-klt-c-password-field-reveal-trigger';
const ATTRIBUTE_PASSWORD_REVEAL_TRIGGER_TEXT = 'data-klt-c-password-field-reveal-trigger-text';
const ATTRIBUTE_PASSWORD_REPEAT_TEXT = 'data-klt-c-password-tooltip-repeat-text';
const ATTRIBUTE_CARD_SELECT = 'data-klt-c-card-select';
const ATTRIBUTE_CARD_SELECT_ON_SELECTED = 'data-klt-c-card-select-on-selected';

const CLASS_INPUT_TOOLTIP_INITALIZED = 'klt-c-form-field-tooltip--js';
const CLASS_PASSWORD_REVEAL_TRIGGER_ACTIVE = 'klt-c-password-field__reveal-trigger--active';
const CLASS_PASSWORD_FIELD_JS_REVEAL = 'klt-c-password-field__input--js-reveal';
const CLASS_BUTTON_LOADING = 'klt-c-button--loading';

let forms: HTMLFormElement[] = [];
let isSubmitting = false;

const defaultTooltipPasswordRepeatOptions: TooltipPasswordRepeatOptions = {
    type: TooltipType.PASSWORD_REPEAT,
    checkOnInputId: null,
    onMatchText: 'Good',
    onMismatchText: null,
};

interface TooltipOptions {
    type: TooltipType;
}

interface TooltipPasswordRepeatOptions extends TooltipOptions {
    checkOnInputId: string;
    onMatchText?: string;
    onMismatchText?: string;
}

/**
 * Options for the password reveal trigger
 */
interface PasswordRevealTriggerOptions {
    passwordHideText?: string;
    passwordFieldId?: string;
    passwordRevealText?: string;
}

/**
 * Inits JavaScript enhancements for form fields
 */
export default function init(vendorRoot: string) {
    try {
        initFormFieldControlledByHandler();
        initTooltips(vendorRoot);
        initPasswordReveal();
        initFormPostDisabling();
        initCardSelect();
    } catch (e) {
        console.error(`Couldn't initalize form fields module.`, e);
    }
}

// TODO: Refactor
// That part should be removed and controlled with util.ts as it already holds many handy tools for controlling
// these states.
export function initFormFieldControlledByHandler() {
    const controlledCollections = [];
    const controlledFormFieldElements = document.querySelectorAll(`[${ATTRIBUTE_CONTROLLED_BY}]`);
    for (let i = 0; i < controlledFormFieldElements.length; i++) {
        const controlledElement = controlledFormFieldElements[i];
        const controlledById = controlledElement.getAttribute(`${ATTRIBUTE_CONTROLLED_BY}`);
        const controls = controlledElement.getAttribute(`${ATTRIBUTE_CONTROL_ATTRIBUTE}`) || ControlledAttr.Disabled;

        if (controlledById) {
            const controllerElement = document.getElementById(controlledById);
            if (
                controllerElement &&
                controllerElement instanceof HTMLInputElement &&
                controllerElement.getAttribute('type') === 'checkbox'
            ) {
                // Switch to default state
                // Default state in case of controlling disabled attribute has to be 'enabled'.
                const currentStateOfCheckbox = controllerElement.checked;
                changeControlState(controls, currentStateOfCheckbox, controlledElement);

                controllerElement.addEventListener('change', function (event) {
                    event.stopPropagation();
                    changeControlState(controls, this.checked, controlledElement);
                });
            }
        }
    }
}

/**
 * Adds interactive tooltip functionality to form inputs with a specific attribute.
 * TODO: A11y: Add aria [aria-hidden="true"] handling
 * TODO: A11y: Bind ESC to close tooltip
 */
export function initTooltips(vendorRoot: string) {
    const fieldsWithInputs = document.querySelectorAll(`[${ATTRIBUTE_INPUT_TOOLTIP}]`);

    if (fieldsWithInputs.length > 0) {
        for (let i = 0; i < fieldsWithInputs.length; i++) {
            const field = fieldsWithInputs[i] as HTMLInputElement;
            const tooltipId = field.getAttribute('aria-describedby');
            const tooltip = document.getElementById(tooltipId);

            if (tooltip) {
                const rawOptions = tooltip.getAttribute(ATTRIBUTE_PASSWORD_TOOLTIP);

                if (!rawOptions) {
                    return;
                }

                const options: TooltipOptions = JSON.parse(rawOptions) as TooltipOptions;

                switch (options.type) {
                    case TooltipType.PASSWORD_STRENGTH:
                        // initPasswordStrength(field, tooltip, vendorRoot, options as TooltipPasswordStrengthOptions);
                        break;
                    case TooltipType.PASSWORD_REPEAT:
                        initPasswordRepeat(field, tooltip, options as TooltipPasswordRepeatOptions);
                        break;
                }
            }
        }
    }
}

export function initPasswordRepeat(
    field: HTMLInputElement,
    tooltip: HTMLElement,
    options: TooltipPasswordRepeatOptions = defaultTooltipPasswordRepeatOptions
) {
    const tooltipId = field.getAttribute('aria-describedby');
    const tooltipTextElement = tooltip.querySelector(`[${ATTRIBUTE_PASSWORD_REPEAT_TEXT}]`);

    if (!options.checkOnInputId) {
        return;
    }

    const fieldToCompare = document.getElementById(options.checkOnInputId) as HTMLInputElement;

    if (!fieldToCompare) {
        return;
    }

    let tooltipTimer = null;
    field.addEventListener(
        'input',
        () => {
            clearTimeout(tooltipTimer);
            tooltipTimer = setTimeout(() => {
                const isPasswordIdentical = field.value === fieldToCompare.value;

                if (tooltipTextElement) {
                    if (options.onMatchText && isPasswordIdentical) {
                        tooltipTextElement.innerHTML = options.onMatchText;
                        tooltip.classList.add(CLASS_INPUT_TOOLTIP_INITALIZED);
                    } else if (!isPasswordIdentical) {
                        tooltipTextElement.innerHTML = options.onMismatchText || '';
                        tooltip.classList.remove(CLASS_INPUT_TOOLTIP_INITALIZED);
                    }
                }
            }, 0);
        },
        false
    );
}

export function initPasswordReveal(element?: HTMLElement) {
    if (element) {
        throw Error('Not implemented');
    } else {
        const passwordRevealTriggers = document.querySelectorAll(`[${ATTRIBUTE_PASSWORD_REVEAL_TRIGGER}]`);

        for (let i = 0; i < passwordRevealTriggers.length; i++) {
            const trigger: Element = passwordRevealTriggers[i];
            const triggerTextElement = trigger.querySelector(`[${ATTRIBUTE_PASSWORD_REVEAL_TRIGGER_TEXT}]`);
            const optionsRaw = trigger.getAttribute(`${ATTRIBUTE_PASSWORD_REVEAL_TRIGGER}`);

            // TODO: Remove aria-label when password is shown

            // Can be configured via HTML data-tag. So get the options
            if (optionsRaw) {
                let options: PasswordRevealTriggerOptions;
                try {
                    options = JSON.parse(optionsRaw);
                } catch (e) {
                    console.error('klt/form-fields: Invalid JSON in password field options');
                }

                if (options && options.passwordFieldId) {
                    const passwordField = document.getElementById(options.passwordFieldId);

                    if (passwordField) {
                        passwordField.classList.add(`${CLASS_PASSWORD_FIELD_JS_REVEAL}`);

                        trigger.addEventListener('click', (event: Event) => {
                            const fieldType = passwordField.getAttribute('type');
                            passwordField.setAttribute('type', fieldType === 'password' ? 'text' : 'password');

                            if (fieldType === 'password') {
                                trigger.classList.add(`${CLASS_PASSWORD_REVEAL_TRIGGER_ACTIVE}`);
                                if (triggerTextElement && options.passwordHideText) {
                                    triggerTextElement.innerHTML = options.passwordHideText;
                                }
                            } else {
                                trigger.classList.remove(`${CLASS_PASSWORD_REVEAL_TRIGGER_ACTIVE}`);
                                if (triggerTextElement && options.passwordRevealText) {
                                    triggerTextElement.innerHTML = options.passwordRevealText;
                                }
                            }
                        });
                    }
                }
            } else {
                break;
            }
        }
    }
}

/**
 * Disables the buttons of a form on post.
 * For Button Components, it adds an additional class
 * to show a loading indicator.
 */
export function initFormPostDisabling() {
    const forms = [...document.querySelectorAll('form:not([method=dialog], [target=_blank])')];
    if (forms && forms.length > 0) {
        for (let iForms = 0; iForms < forms.length; iForms++) {
            const form = forms[iForms];
            let formSent = false;

            form.addEventListener('submit', (event) => {
                if (isSubmitting) {
                    event.preventDefault();
                    return;
                }

                // Without using timeout, the submit button would directly be set to
                // disabled. Now when a browser decides to trigger this handler here first,
                // with the timeout we ensure that name/value attributes on the button can be submitted
                // too by any ajax calls, which wouldn't be possible when the button has a disabled state at the moment
                // any module want to submit all the form data. Inputs/Buttons that are disabled are not part of
                // the submitted form data.
                setTimeout(() => lockForm(form), 10);
            });
        }
    }
}

/**
 * Puts form in locking state for when user submitted it.
 * Disables all submit functionality to prevent double form posts.
 */
export const lockForm = (form: HTMLFormElement) => setFormLockState(form, true);

/**
 * Unlocks form for continuous submits after it was locked by the form submit routine
 */
export const unlockForm = (form: HTMLFormElement) => setFormLockState(form, false);

/**
 * Search card select components and initalize JavaScript progressive enhancements.
 * Part of the progressive enhancements are applying client-side conditions like
 * single selection rows in multi-choice card selects and resetting styles.
 */
export function initCardSelect() {
    const forms = getForms();

    // get additional elements that react to a change in the card selects
    const coupledElements = document.querySelectorAll(`[${ATTRIBUTE_CARD_SELECT_ON_SELECTED}]`);

    forms.forEach((form) => {
        const cardSelect = form.querySelector(`[${ATTRIBUTE_CARD_SELECT}]`);
        if (cardSelect) {
            const radioButtons = cardSelect.querySelectorAll<HTMLInputElement>('input[type="radio"]');
            const submitButton = form.querySelector<HTMLButtonElement>('button[type="submit"]');
            const fieldsets = cardSelect.querySelectorAll<HTMLFieldSetElement>('fieldset');
            const isMultiChoice = fieldsets.length > 1;

            let currentlyCheckedRadioButtons: HTMLInputElement[] = [];

            const hasSelection = (rdoButtons: NodeListOf<HTMLInputElement>) =>
                Array.prototype.slice.call(rdoButtons).filter((radioButton) => radioButton.checked).length > 0;
            const formDisabledHandler = (event: Event) => event.preventDefault();

            // Blocks form submission based on whether there are radio buttons
            // selected or not
            const setFormDisabledState = (state: boolean) => {
                if (state) {
                    submitButton.setAttribute('disabled', 'disabled');
                    form.addEventListener('submit', formDisabledHandler);
                    cardSelect.classList.remove('klt-c-card-select--has-selection');
                } else {
                    submitButton.removeAttribute('disabled');
                    form.removeEventListener('submit', formDisabledHandler);
                    cardSelect.classList.add('klt-c-card-select--has-selection');
                }
            };

            // Resets the visual state of the fieldsets based on the checked/unchecked
            // states of the child radio boxes.
            const refreshFieldsetStates = () => {
                fieldsets.forEach((fieldset) => {
                    if (hasSelection(fieldset.querySelectorAll('input[type="radio"]'))) {
                        fieldset.classList.add('klt-c-card-select-fieldset--has-selection');
                    } else {
                        fieldset.classList.remove('klt-c-card-select-fieldset--has-selection');
                    }
                });
            };

            // Set the initial state for when the radio buttons
            // are preset server-side e.g. for after validation error
            // We execute it in `setTimeout` so that the correct value is taken also when it is restored from the browser back action in Chrominium
            setTimeout(() => setFormDisabledState(!hasSelection(radioButtons)), 0);
            refreshFieldsetStates();

            // Why for loops and not forEach? Because for loops are faster.
            // And because we loop quite often here, it might make a minimal difference.
            for (let ifs = 0; ifs < fieldsets.length; ifs++) {
                const fieldsetRadioButtons = fieldsets[ifs].querySelectorAll<HTMLInputElement>('input[type="radio"]');

                for (let ib = 0; ib < fieldsetRadioButtons.length; ib++) {
                    const radioButton = fieldsetRadioButtons[ib];

                    // use click handler instead of change handler to allow unselecting
                    // a radio button
                    // https://gitlab.com/KuBch/m-und-p/issues/549
                    // As a possible future improvement you might want to use document
                    // wide event delegation to not bloat the browser with a click event
                    // for every single radio button, but check click event on a global
                    // level and apply the radio button actions
                    radioButton.addEventListener('click', function (event) {
                        const radioButtonValue = this.value;

                        if (this.checked && currentlyCheckedRadioButtons.indexOf(this) !== -1) {
                            // Clicking a selected radio button should unselect it
                            this.checked = false;
                            currentlyCheckedRadioButtons.splice(currentlyCheckedRadioButtons.indexOf(this));
                        } else {
                            // In case multiple radio boxes have the same value, unckeck other radio boxes
                            // with the same value matching the current selected. This is for example the
                            // case in multi-select card selects.
                            for (let ri = 0; ri < radioButtons.length; ri++) {
                                if (radioButtons[ri] !== this && radioButtons[ri].value === radioButtonValue) {
                                    radioButtons[ri].checked = false;
                                }

                                const itemIndex = currentlyCheckedRadioButtons.indexOf(radioButtons[ri]);
                                if (itemIndex !== -1 && !radioButtons[ri].checked) {
                                    currentlyCheckedRadioButtons.splice(itemIndex);
                                }
                            }

                            currentlyCheckedRadioButtons.push(this);
                        }

                        // Re-evaluate state over all radio boxes as the change of one radio box
                        // has an effect on the whole form.
                        setFormDisabledState(!hasSelection(radioButtons));
                        refreshFieldsetStates();

                        // By adding ATTRIBUTE_CARD_SELECT_ON_SELECTED value as attribute to any
                        // element in the DOM, the element will toggle the property value
                        // of given attribute when a address is selected, e.g.
                        // <button data-klt-c-card-select-on-selected="disabled">Hi</button>
                        if (coupledElements && coupledElements.length > 0) {
                            for (let ice = 0; ice < coupledElements.length; ice++) {
                                const element = coupledElements[ice];
                                const attributeToToggle = element.getAttribute(ATTRIBUTE_CARD_SELECT_ON_SELECTED);

                                if (element instanceof HTMLButtonElement) {
                                    if (hasSelection) {
                                        element.setAttribute(attributeToToggle, 'true');
                                    } else {
                                        element.removeAttribute(attributeToToggle);
                                    }
                                } else if (element instanceof HTMLAnchorElement) {
                                    // A anchor element without href is a disabled link and valid
                                    // according to the specification. Save the href in a data- attribute
                                    // to be able revert the removal later.
                                    if (hasSelection) {
                                        element.setAttribute('data-href', element.getAttribute('href'));
                                        element.removeAttribute('href');
                                    } else {
                                        element.setAttribute('href', element.getAttribute('data-href'));
                                        element.removeAttribute('data-href');
                                    }
                                }
                            }
                        }
                    });
                }
            }
        }
    });
}

function changeControlState(controls: ControlledAttr | string, state: boolean, controlledElement: Element) {
    switch (controls) {
        case ControlledAttr.Visibility:
            setVisibility(state, controlledElement);
            break;
        case ControlledAttr.Disabled:
            setDisabled(state, controlledElement);
            break;
    }
}

function setDisabledStateOnChildren(controlledElement: Element, state: boolean) {
    const inputElements = controlledElement.getElementsByTagName('input');
    const textareaElements = controlledElement.getElementsByTagName('textarea');
    let elements = [];
    if (inputElements.length > 0) {
        elements = [...elements, inputElements];
    }
    if (textareaElements.length > 0) {
        elements = [...elements, textareaElements];
    }

    for (let iCollections = 0; iCollections < elements.length; iCollections++) {
        for (let iElements = 0; iElements < elements[iCollections].length; iElements++) {
            const inputElement = elements[iCollections][iElements];

            if (state) {
                inputElement.setAttribute('disabled', '');
            } else {
                inputElement.removeAttribute('disabled');
            }
        }
    }
}

function setVisibility(visible: boolean, element: Element) {
    if (visible) {
        if (element instanceof HTMLInputElement) {
            element.removeAttribute('disabled');
            element.classList.remove('klt-c-form-fields--hidden');
        } else {
            element.classList.remove('klt-c-form-fields--hidden');
            setDisabledStateOnChildren(element, false);
        }
    } else {
        if (element instanceof HTMLInputElement) {
            hideControlledElement(element);
        } else {
            element.classList.add('klt-c-form-fields--hidden');
            setDisabledStateOnChildren(element, true);
        }
    }
}

function setDisabled(disabled: boolean, element: Element) {
    if (disabled) {
        if (element instanceof HTMLInputElement) {
            element.setAttribute('disabled', '');
        } else {
            element.classList.add('klt-c-form-fields-disabled');
            setDisabledStateOnChildren(element, true);
        }
    } else {
        if (element instanceof HTMLInputElement) {
            element.removeAttribute('disabled');
        } else {
            element.classList.remove('klt-c-form-fields-disabled');
            setDisabledStateOnChildren(element, false);
        }
    }
}

function hideControlledElement(controlledElement: HTMLElement | HTMLInputElement | Element) {
    controlledElement.setAttribute('disabled', '');
    controlledElement.classList.add('klt-c-form-fields--hidden');
}

function getForms(): HTMLFormElement[] {
    if (forms && forms.length > 0) {
        return forms;
    }

    const formsNodeList = document.getElementsByTagName('form:not([method=dialog], [target=_blank])');
    forms = Array.prototype.slice.call(formsNodeList);
    return forms;
}

function setFormLockState(form: HTMLFormElement, locked: boolean) {
    const buttons = form.querySelectorAll<HTMLButtonElement>('button[type="submit"]');
    isSubmitting = locked;

    if (buttons && buttons.length > 0) {
        for (let iButtons = 0; iButtons < buttons.length; iButtons++) {
            const button = buttons[iButtons];
            const buttonNeedsLoadingIndicator = button.classList.contains('klt-c-button');
            if (buttonNeedsLoadingIndicator) {
                button.disabled = locked;

                if (locked) {
                    button.classList.add(CLASS_BUTTON_LOADING);
                } else {
                    button.classList.remove(CLASS_BUTTON_LOADING);
                }
            }
        }
    }
}
